import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';

export interface Category {
  id: string;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
}

export interface Parish {
  id: string;
  name: string;
  slug: string;
  center: {
    lat: number;
    lng: number;
  };
  boundaries?: any;
}

export const api = {
  categories: {
    getAll: async (): Promise<Category[]> => {
      const response = await axios.get(`${API_BASE_URL}/categories`);
      return response.data.data.categories;
    },
  },
  parishes: {
    getAll: async (): Promise<Parish[]> => {
      const response = await axios.get(`${API_BASE_URL}/parishes`);
      return response.data.data.parishes;
    },
    getByLocation: async (lat: number, lng: number): Promise<Parish | null> => {
      try {
        const response = await axios.get(`${API_BASE_URL}/parishes/by-location`, {
          params: { lat, lng },
        });
        return response.data.data.parish;
      } catch (error: any) {
        if (error.response?.status === 404) {
          return null;
        }
        throw error;
      }
    },
  },
  voting: {
    createSession: async (
      projectId: string,
      sessionData: {
        sessionNumber: number;
        type: 'open' | 'anonymous';
        question: string;
        options: Array<{ id: string; label: string }>;
        startDate: string;
        endDate?: string;
        geoRestricted?: boolean;
        allowedParishes?: string[];
        connectedGoals?: string[];
        connectedMilestones?: string[];
        connectedDocuments?: string[];
      },
      token: string
    ) => {
      const response = await axios.post(
        `${API_BASE_URL}/votes/projects/${projectId}/sessions`,
        sessionData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.data.session;
    },
    updateSession: async (
      projectId: string,
      sessionId: string,
      updates: {
        question?: string;
        startDate?: string;
        endDate?: string;
        type?: 'open' | 'anonymous';
      },
      token: string
    ) => {
      const response = await axios.put(
        `${API_BASE_URL}/votes/projects/${projectId}/sessions/${sessionId}`,
        updates,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.data.session;
    },
  },
  projectFiles: {
    getProjectFiles: async (projectId: string, includeDeleted?: boolean, token?: string): Promise<any[]> => {
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const params = includeDeleted ? { includeDeleted: 'true' } : {};
      const response = await axios.get(
        `${API_BASE_URL}/project-files/projects/${projectId}/files`,
        { ...config, params }
      );
      return response.data.data.files;
    },
    getFileById: async (fileId: string, token?: string): Promise<any> => {
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const response = await axios.get(
        `${API_BASE_URL}/project-files/files/${fileId}`,
        config
      );
      return response.data.data.file;
    },
    createFile: async (
      projectId: string,
      fileData: {
        type: 'image' | 'video' | 'document';
        filename: string;
        originalFilename: string;
        url: string;
        thumbnailUrl?: string;
        mimeType: string;
        size: number;
        description?: string;
        synopsis?: string;
      },
      token: string
    ): Promise<any> => {
      const response = await axios.post(
        `${API_BASE_URL}/project-files/projects/${projectId}/files`,
        fileData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.data.file;
    },
    updateFile: async (
      projectId: string,
      fileId: string,
      updates: {
        description?: string;
        synopsis?: string;
        changeNotes?: string;
      },
      file?: File,
      token?: string
    ): Promise<any> => {
      const formData = new FormData();
      if (file) {
        formData.append('file', file);
      }
      if (updates.description !== undefined) {
        formData.append('description', updates.description);
      }
      if (updates.synopsis !== undefined) {
        formData.append('synopsis', updates.synopsis);
      }
      if (updates.changeNotes !== undefined) {
        formData.append('changeNotes', updates.changeNotes);
      }

      const config = token
        ? { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
        : { headers: { 'Content-Type': 'multipart/form-data' } };

      const response = await axios.put(
        `${API_BASE_URL}/project-files/projects/${projectId}/files/${fileId}`,
        formData,
        config
      );
      return response.data.data.file;
    },
    deleteFile: async (projectId: string, fileId: string, reason?: string, token?: string): Promise<void> => {
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      await axios.delete(
        `${API_BASE_URL}/project-files/projects/${projectId}/files/${fileId}`,
        { ...config, data: { reason } }
      );
    },
    restoreFile: async (projectId: string, fileId: string, token: string): Promise<any> => {
      const response = await axios.post(
        `${API_BASE_URL}/project-files/projects/${projectId}/files/${fileId}/restore`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.data.file;
    },
  },
  comments: {
    getComments: async (
      entityType: 'project' | 'projectFile',
      entityId: string,
      options?: { page?: number; limit?: number; parentId?: string },
      token?: string
    ): Promise<{ comments: any[]; pagination: any }> => {
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const response = await axios.get(
        `${API_BASE_URL}/${entityType}/${entityId}/comments`,
        { ...config, params: options }
      );
      return response.data.data;
    },
    createComment: async (
      entityType: 'project' | 'projectFile',
      entityId: string,
      data: { content: string; parentId?: string; rating?: number },
      token: string
    ): Promise<any> => {
      const response = await axios.post(
        `${API_BASE_URL}/${entityType}/${entityId}/comments`,
        data,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.data.comment;
    },
    updateComment: async (
      commentId: string,
      data: { content?: string; rating?: number },
      token: string
    ): Promise<any> => {
      const response = await axios.put(
        `${API_BASE_URL}/comments/${commentId}`,
        data,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.data.comment;
    },
    deleteComment: async (commentId: string, token: string): Promise<void> => {
      await axios.delete(`${API_BASE_URL}/comments/${commentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    hideComment: async (commentId: string, reason?: string, token?: string): Promise<any> => {
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const response = await axios.post(
        `${API_BASE_URL}/comments/${commentId}/hide`,
        { reason },
        config
      );
      return response.data.data.comment;
    },
    unhideComment: async (commentId: string, token: string): Promise<any> => {
      const response = await axios.post(
        `${API_BASE_URL}/comments/${commentId}/unhide`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.data.comment;
    },
    addReaction: async (
      commentId: string,
      type: string,
      token: string
    ): Promise<{ reactionSummary: Record<string, number>; userReaction: string | null }> => {
      const response = await axios.post(
        `${API_BASE_URL}/comments/${commentId}/reactions`,
        { type },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.data;
    },
  },
  reactionTypes: {
    getAll: async (token?: string): Promise<any[]> => {
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const response = await axios.get(`${API_BASE_URL}/reaction-types`, config);
      return response.data.data.reactionTypes;
    },
  },
  chat: {
    getMessages: async (
      projectId: string,
      options?: { limit?: number; before?: string },
      token?: string
    ): Promise<any[]> => {
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const response = await axios.get(
        `${API_BASE_URL}/projects/${projectId}/chat`,
        { ...config, params: options }
      );
      return response.data.data.messages;
    },
    sendMessage: async (
      projectId: string,
      data: { content: string; parentId?: string },
      token: string
    ): Promise<any> => {
      const response = await axios.post(
        `${API_BASE_URL}/projects/${projectId}/chat`,
        data,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.data.message;
    },
    editMessage: async (
      messageId: string,
      content: string,
      token: string
    ): Promise<any> => {
      const response = await axios.put(
        `${API_BASE_URL}/chat/messages/${messageId}`,
        { content },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.data.message;
    },
    deleteMessage: async (messageId: string, token: string): Promise<void> => {
      await axios.delete(`${API_BASE_URL}/chat/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    addReaction: async (
      messageId: string,
      type: string,
      token: string
    ): Promise<{ reactionSummary: Record<string, number>; userReaction: string | null }> => {
      const response = await axios.post(
        `${API_BASE_URL}/chat/messages/${messageId}/reactions`,
        { type },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.data;
    },
  },
};

